import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import Box from '../Box';
import View from '../View';
import Text from '../Text';
import Heading from '../Heading';
import type { ViewProps } from '../View/View';
import type { BoxProps } from '../Box/Box';
import {
  border,
  color,
  flexbox,
  layout,
  position,
  space,
  typography,
} from 'styled-system';
import {
  FormVisibilityToggle,
  useFormVisibility,
  VisibilityOverlay,
} from './FormSettings';

type FormProps =
  | ({
      isForm?: true;
      children: React.ReactNode;
    } & BoxProps &
      React.FormHTMLAttributes<HTMLFormElement>)
  | ({
      isForm: false;
      children: React.ReactNode;
    } & BoxProps &
      React.HTMLAttributes<HTMLDivElement>);

const FormInner = styled.form<FormProps>(
  {
    boxSizing: 'border-box',
    minWidth: 0,
  },
  border,
  color,
  flexbox,
  layout,
  position,
  space,
  typography,
);

const Form = forwardRef(
  (props: FormProps, ref?: React.Ref<HTMLFormElement>): JSX.Element =>
    props.isForm === false ? (
      <Box
        width={1}
        bg="form.background"
        color="text"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="form.border"
        {...props}
      />
    ) : (
      <FormInner
        ref={ref}
        width={1}
        bg="form.background"
        color="text"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="form.border"
        {...props}
      />
    ),
);

const SectionHeading = styled(Heading)`
  font-size: 1rem;
  margin-top: 0;
  margin-bottom: 16px;
  font-weight: bold;
`;

export const FormTitle = (
  props: React.PropsWithChildren<Record<string, unknown>>,
): JSX.Element => <Heading pl={2} pr={4} py={0} m={0} {...props} />;

type SectionProps = {
  children: React.ReactNode;
  title?: string | JSX.Element;
  muted?: boolean;
  visibilityId?: string;
};

export const FormSection = ({
  children,
  title,
  muted = false,
  visibilityId,
  ...restProps
}: SectionProps & ViewProps) => {
  const {
    isEditingVisibility,
    isVisible,
    isVisibilityEditEnabled,
    onVisibilityToggle,
  } = useFormVisibility(visibilityId);

  if (!isVisible && !isVisibilityEditEnabled) return null;

  return (
    <Box
      as="section"
      display="flex"
      flexDirection="column"
      p={4}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderBottomColor={'form.border'}
      bg={muted ? 'form.mutedBackground' : 'form.transparent'}
      {...restProps}
    >
      {isVisibilityEditEnabled && (
        <FormVisibilityToggle
          isVisible={isVisible}
          onClick={onVisibilityToggle}
        />
      )}

      {title && <SectionHeading level="2">{title}</SectionHeading>}

      {isEditingVisibility ? (
        <VisibilityOverlay
          isEditingVisibility={isEditingVisibility}
          isVisible={isVisible}
        >
          {children}
        </VisibilityOverlay>
      ) : (
        children
      )}
    </Box>
  );
};

type FieldProps = {
  children: React.ReactNode;
  label?: JSX.Element | string;
  required?: boolean;
  hasError?: boolean;
  title?: string;
  /** Text rendered after the field's children  */
  helpText?: string | React.ReactNode;
  /** Unique field id used for the form visibility toggle feature */
  visibilityId?: string;
  /** Warnings rendered next to the field label */
  notifications?: React.ReactNode;
};

export const FormField = ({
  children,
  label,
  required = false,
  hasError = false,
  helpText,
  visibilityId,
  notifications,
  ...restProps
}: FieldProps & BoxProps): JSX.Element | null => {
  const {
    isEditingVisibility,
    isVisible,
    isVisibilityEditEnabled,
    onVisibilityToggle,
  } = useFormVisibility(visibilityId);

  if (!isVisible && !isVisibilityEditEnabled) return null;

  return (
    <Box
      display="flex"
      fontSize="0.875rem"
      width={1}
      flexDirection="row"
      mr={3}
      mb={3}
      zIndex="unset"
      {...restProps}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        width={1}
        zIndex="unset"
      >
        <Box
          display="flex"
          width={1}
          mb={2}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          {isVisibilityEditEnabled && (
            <FormVisibilityToggle
              isVisible={isVisible}
              onClick={onVisibilityToggle}
            />
          )}
          <Text
            fontWeight="bold"
            isSingleLine
            width="100%"
            overflow="hidden"
            minHeight={21}
            {...(hasError && { color: '#9d0300' })}
          >
            {label ? label : '\u200b'}
          </Text>
          {required && (
            <Text color="orange.orange_600" fontSize="1">
              Required
            </Text>
          )}
          {notifications}
        </Box>
        {isEditingVisibility ? (
          <VisibilityOverlay
            disablePointerEventsWhileEditing
            isEditingVisibility={isEditingVisibility}
            isVisible={isVisible}
          >
            {children}
          </VisibilityOverlay>
        ) : (
          children
        )}
        {helpText && (
          <Text
            fontSize="1"
            color="neutral.gray_250"
            mt={1}
            isSingleLine
            isUnselectable
          >
            {helpText}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export const FormRow = (
  props: React.PropsWithChildren<Record<string, unknown>> & ViewProps,
): JSX.Element => (
  <Box
    display="flex"
    width={1}
    flexDirection="row"
    alignItems="flex-start"
    flexWrap="wrap"
    zIndex="unset"
    {...props}
  />
);

export const FormInfoBox = styled(View)`
  background-color: rgba(255, 255, 255, 0.5);
  border-width: 1px;
  border-style: dashed;
  border-color: ${themeGet('colors.form.border')};
  padding: ${themeGet('space.3')}px;
`;

export const FormValue = styled(Text)`
  height: 32px;
`;

export const FormSectionHeading = SectionHeading;

export default Form;
